import { useState } from "react";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import useTranslation from "@babackapp/translations/use-translation";
import { Input } from "@babackapp/ui/components/input";
import Tooltip from "@/components-legacy/tooltip";

type WorkflowInputTextProps = {
  onChangeTitle: (value: string) => void;
  onChangeArea?: (value: string) => void;
  onChangeCustom?: (customFields: { id: string; value: string }[]) => void;
  title: string;
  textArea?: string;
  desc?: boolean;
  customFields?: { id: string; value: string }[];
};

export function WorkflowInputText({
  onChangeTitle,
  onChangeArea,
  onChangeCustom,
  title,
  textArea = "",
  customFields: initialCustomFields = [],
}: WorkflowInputTextProps) {
  const [customFields, setCustomFields] =
    useState<{ id: string; value: string }[]>(initialCustomFields);

  const t = useTranslation();

  const handleCustomFieldChange = (
    index: number,
    field: "id" | "value",
    value: string,
  ) => {
    const updatedCustomFields = [...customFields];
    updatedCustomFields[index][field] = value;
    setCustomFields(updatedCustomFields);
    onChangeCustom?.(updatedCustomFields);
  };

  const handleAddCustomField = () => {
    const newField = { id: "", value: "" };
    const updatedCustomFields = [...customFields, newField];
    setCustomFields(updatedCustomFields);
    onChangeCustom?.(updatedCustomFields);
  };

  const handleRemoveCustomField = (index: number) => {
    const updatedCustomFields = customFields.filter((_, i) => i !== index);
    setCustomFields(updatedCustomFields);
    onChangeCustom?.(updatedCustomFields);
  };

  return (
    <div className="text-sm">
      <div>
        {t("workflow.module.subject")}
        <Input value={title} onChange={(e) => onChangeTitle(e.target.value)} />
      </div>
      {onChangeArea && (
        <div className="mt-2 text-sm">
          {t("workflow.module.desc")}
          <textarea
            value={textArea}
            onChange={(e) => onChangeArea?.(e.target.value)}
            className="block p-2.5 mb-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      )}
      <div className="flex flex-col pt-3">
        <div className="text-gray-500">
          {t("workflow.module.zendesk.addedInput")}
        </div>
        <div className="flex flex-col space-y-4">
          {customFields.map((cf, index) => (
            <div
              key={index}
              className="flex group items-center justify-between"
            >
              <div className="space-y-2">
                <Input
                  className="flex-1"
                  prefix="Id :"
                  value={cf.id || ""}
                  onChange={(e) =>
                    handleCustomFieldChange(index, "id", e.target.value)
                  }
                />
                <Input
                  prefix="Value :"
                  value={cf.value}
                  onChange={(e) =>
                    handleCustomFieldChange(index, "value", e.target.value)
                  }
                />
              </div>
              <TrashIcon
                onClick={() => handleRemoveCustomField(index)}
                className="h-5 w-5 invisible text-gray-400 cursor-pointer hover:text-red-600 group-hover:visible"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex pt-4 place-content-center">
        <PlusCircleIcon
          onClick={handleAddCustomField}
          className="text-gray-400 h-5 w-5 cursor-pointer hover:text-gray-600"
        />
      </div>
      <Tooltip
        description={
          <div className="text-sm bg-white gap-2 flex flex-col">
            <div><span className="font-bold">{"{shopId}"}</span> - {t("workflow.module.shopId")}</div>
            <div><span className="font-bold">{"{shopProvider}"}</span> - {t("workflow.module.shopProvider")}</div>
            <div>
              <span className="font-bold">{"{returnRequestId}"}</span> - {t("workflow.module.returnRequestId")}
            </div>
            <div><span className="font-bold">{"{orderName}"}</span> - {t("workflow.module.orderName")}</div>
            <div><span className="font-bold">{"{orderId}"}</span> - {t("workflow.module.orderId")}</div>
            <div><span className="font-bold">{"{trackingCode}"}</span> - {t("workflow.module.trackingCode")}</div>
            <div><span className="font-bold">{"{resolutions}"}</span> - {t("workflow.module.resolutions")}</div>
            <div><span className="font-bold">{"{firstName}"}</span> - {t("workflow.module.firstName")}</div>
            <div><span className="font-bold">{"{lastName}"}</span> - {t("workflow.module.lastName")}</div>
          </div>
        }
        className="text-xs text-gray-400"
      >
        <p>{t("workflow.module.asterisk")}</p>
      </Tooltip>
    </div>
  );
}
